/**
 * Fleet API fetch functions for the /now page.
 *
 * All functions use apiFetch (the authenticated fleet API client with
 * refresh-on-401 interceptor) and validate responses against Zod schemas
 * for runtime safety.
 *
 * Data contracts from fleet-api Plan 03-01:
 *   GET /v1/sites                              -> SiteSummary[]
 *   GET /v1/sites/{id}/live-state              -> LiveState
 *   GET /v1/insights/deterrence?site_id&window -> DeterrenceKPIs
 *   GET /v1/alerts?site_id&since&limit         -> AlertSummary[]
 */
import { z } from 'zod';
import { apiFetch } from '@/lib/api-client';
import {
  DeterrenceKPIsSchema,
  DeterrenceBreakdownResponseSchema,
  LiveStateSchema,
  AlertSummarySchema,
  SiteSummarySchema,
  SiteDetailSchema,
} from './schemas';
import type {
  DeterrenceKPIs,
  DeterrenceBreakdownPeriod,
  DeterrenceBreakdownOutcomeFilter,
  DeterrenceBreakdownResponse,
  LiveState,
  AlertSummary,
  SiteSummary,
  SiteDetail,
} from './schemas';

/**
 * Fetches the list of sites belonging to the caller's organization.
 *
 * Phase 3: single-site clients; returns the first site. Multi-site UX ships Phase 4.
 *
 * @returns Validated array of SiteSummary objects
 */
export async function fetchSites(): Promise<SiteSummary[]> {
  const raw = await apiFetch<unknown>('/v1/sites');
  return z.array(SiteSummarySchema).parse(raw);
}

/**
 * Fetches the live state snapshot for a specific site.
 *
 * Returns the 4-zone tuple and sensor health array for the concentric viz
 * initial render. Subsequent updates arrive via WebSocket in Plan 03-06.
 *
 * @param siteId - UUID of the target site
 * @returns Validated LiveState object with zones and sensors arrays
 */
export async function fetchLiveState(siteId: string): Promise<LiveState> {
  const raw = await apiFetch<unknown>(`/v1/sites/${siteId}/live-state`);
  return LiveStateSchema.parse(raw);
}

/**
 * Fetches deterrence KPIs for the hero strip.
 *
 * @param siteId - UUID of the target site
 * @param window - Aggregation window (week, month, all); defaults to 'week'
 * @returns Validated DeterrenceKPIs object
 */
export async function fetchDeterrenceKPIs(
  siteId: string,
  window: 'week' | 'month' | 'all' = 'week',
): Promise<DeterrenceKPIs> {
  const raw = await apiFetch<unknown>(
    `/v1/insights/deterrence?site_id=${encodeURIComponent(siteId)}&window=${window}`,
  );
  return DeterrenceKPIsSchema.parse(raw);
}

/**
 * Fetches recent alert summaries for the event ribbon.
 *
 * @param siteId - UUID of the target site
 * @param since - ISO 8601 duration (e.g. 'PT2H' for past 2 hours); defaults to 'PT2H'
 * @param limit - Maximum number of alerts to return; defaults to 200
 * @returns Validated array of AlertSummary objects sorted by ts ascending
 */
export async function fetchRecentAlerts(
  siteId: string,
  since: string = 'PT2H',
  limit: number = 200,
): Promise<AlertSummary[]> {
  const raw = await apiFetch<unknown>(
    `/v1/alerts?site_id=${encodeURIComponent(siteId)}&since=${since}&limit=${limit}`,
  );
  return z.array(AlertSummarySchema).parse(raw);
}

/**
 * Fetches full site detail including geographic location.
 *
 * Required by Plan 03-07 for the FrozenMapSnapshot component.
 * Parses and validates against SiteDetailSchema which includes the
 * optional location: {lat, lng} field.
 *
 * @param siteId - UUID of the target site
 * @returns Validated SiteDetail object including optional location
 */
export async function fetchSiteDetail(siteId: string): Promise<SiteDetail> {
  const raw = await apiFetch<unknown>(`/v1/sites/${encodeURIComponent(siteId)}`);
  return SiteDetailSchema.parse(raw);
}

/**
 * Fetches the paginated deterrence breakdown for the Performance drawer.
 *
 * Plan 12-11 consumer of the Plan 12-10 endpoint:
 *   GET /v1/insights/deterrence/breakdown?site_id&period&outcome&limit&offset
 *
 * Cross-org calls return 404 (handled by the caller; the drawer renders
 * an empty state when items is empty). The server enforces 1 <= limit <= 200;
 * the drawer always requests limit=25 which is well within that range.
 *
 * @param params - Query parameters mirroring the Pydantic schema
 * @returns Validated DeterrenceBreakdownResponse envelope
 */
export async function fetchDeterrenceBreakdown(params: {
  site_id: string;
  period?: DeterrenceBreakdownPeriod;
  outcome?: DeterrenceBreakdownOutcomeFilter;
  limit?: number;
  offset?: number;
}): Promise<DeterrenceBreakdownResponse> {
  const sp = new URLSearchParams();
  sp.set('site_id', params.site_id);
  if (params.period) sp.set('period', params.period);
  if (params.outcome) sp.set('outcome', params.outcome);
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  if (params.offset !== undefined) sp.set('offset', String(params.offset));
  const raw = await apiFetch<unknown>(
    `/v1/insights/deterrence/breakdown?${sp.toString()}`,
  );
  return DeterrenceBreakdownResponseSchema.parse(raw);
}
